import React, { useState, useEffect } from 'react';
import { 
  Plus, Search, Download, Eye, Edit, Trash2, FileText, 
  Calendar, Building, DollarSign, Hash, User, Filter, CheckCircle, XCircle, FileCheck
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import DataTable from '../../components/DataTable/DataTable';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import Breadcrumb from '../../components/Common/Breadcrumb';
import { toast } from 'react-toastify';
import proformaInvoiceService from '../../services/proformaInvoiceService';

const ProformaInvoice = () => {
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteItem, setDeleteItem] = useState(null);
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancelItem, setCancelItem] = useState(null);
  const [stats, setStats] = useState({
    totalInvoices: 0,
    draftInvoices: 0,
    approvedInvoices: 0,
    cancelledInvoices: 0,
    convertedInvoices: 0,
    totalValue: 0,
    totalTaxAmount: 0,
    totalAmountWithTax: 0
  });
  const [pagination, setPagination] = useState({
    current: 1,
    pages: 1,
    total: 0
  });

  // Load data on component mount
  useEffect(() => {
    loadData();
    loadStats();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await proformaInvoiceService.getAllProformaInvoices({
        page: pagination.current,
        limit: 10,
        search: searchTerm
      });

      if (response.success) {
        // Transform _id to id for consistency
        const transformedData = (response.data || []).map(invoice => ({
          ...invoice,
          id: invoice._id || invoice.id
        }));
        setData(transformedData);
        setPagination(response.pagination);
      } else {
        toast.error('Failed to load proforma invoices');
      }
    } catch (error) {
      console.error('Error loading proforma invoices:', error);
      toast.error('Failed to load proforma invoices');
    } finally {
      setLoading(false);
    }
  };

  const loadStats = async () => {
    try {
      const response = await proformaInvoiceService.getProformaInvoiceStats();
      if (response.success) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    }
  };

  // Search functionality
  const handleSearch = () => {
    setPagination(prev => ({ ...prev, current: 1 }));
    loadData();
  };

  // Pagination
  const handlePageChange = (page) => {
    setPagination(prev => ({ ...prev, current: page }));
    loadData();
  };

  // Filter data based on search term (for client-side filtering if needed)
  const filteredData = data.filter(item =>
    item.proformaInvoiceNumber?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customerName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.PONo?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    item.customerCode?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Table columns configuration
  const columns = [
    {
      key: 'proformaInvoiceNumber',
      label: 'Invoice #',
      sortable: true,
      render: (value, row) => (
        <div className="flex items-center gap-2">
          <FileCheck size={16} className="text-blue-600" />
          <span className="font-medium text-gray-900">{value}</span>
        </div>
      )
    },
    {
      key: 'proformaInvoiceDate',
      label: 'Invoice Date',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-500" />
          <span>{new Date(value).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'validUntil',
      label: 'Valid Until',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Calendar size={16} className="text-gray-500" />
          <span>{new Date(value).toLocaleDateString()}</span>
        </div>
      )
    },
    {
      key: 'PONo',
      label: 'PO No.',
      sortable: true,
      render: (value) => (
        <div className="flex items-center gap-2">
          <Hash size={16} className="text-gray-500" />
          <span className="font-medium">{value || '-'}</span>
        </div>
      )
    },
    {
      key: 'customerName',
      label: 'Customer Name',
      sortable: true,
      render: (value, row) => (
        <div>
          <div className="font-medium text-gray-900">{value}</div>
          <div className="text-sm text-gray-500">{row.customerCode}</div>
        </div>
      )
    },
    {
      key: 'currency',
      label: 'Currency',
      sortable: true,
      render: (value) => (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
          {value}
        </span>
      )
    },
    {
      key: 'totalAmountWithTax',
      label: 'Total Amount',
      sortable: true,
      render: (value, row) => (
        <div className="text-right">
          <div className="font-medium text-gray-900">
            {row.currency} {value.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
          <div className="text-sm text-gray-500">
            Tax: {row.currency} {row.totalTaxAmount.toLocaleString('en-US', { minimumFractionDigits: 2 })}
          </div>
        </div>
      )
    },
    {
      key: 'status',
      label: 'Status',
      sortable: true,
      render: (value) => {
        const statusConfig = {
          'Draft': { color: 'bg-gray-100 text-gray-800', icon: '📝' },
          'Pending Approval': { color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
          'Approved': { color: 'bg-green-100 text-green-800', icon: '✅' },
          'Sent': { color: 'bg-purple-100 text-purple-800', icon: '📤' },
          'Converted to Tax Invoice': { color: 'bg-blue-100 text-blue-800', icon: '🔄' },
          'Cancelled': { color: 'bg-red-100 text-red-800', icon: '❌' }
        };
        const config = statusConfig[value] || statusConfig['Draft'];
        
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <span>{config.icon}</span>
            {value}
          </span>
        );
      }
    },
    {
      key: 'paymentStatus',
      label: 'Payment',
      sortable: true,
      render: (value) => {
        const paymentConfig = {
          'Unpaid': { color: 'bg-red-100 text-red-800', icon: '💸' },
          'Partially Paid': { color: 'bg-yellow-100 text-yellow-800', icon: '⏳' },
          'Paid': { color: 'bg-green-100 text-green-800', icon: '✅' },
          'Overdue': { color: 'bg-red-100 text-red-800', icon: '⚠️' }
        };
        const config = paymentConfig[value] || paymentConfig['Unpaid'];
        
        return (
          <span className={`inline-flex items-center gap-1 px-2.5 py-0.5 rounded-full text-xs font-medium ${config.color}`}>
            <span>{config.icon}</span>
            {value}
          </span>
        );
      }
    }
  ];

  // Event handlers
  const handleAddNew = () => {
    navigate('/sales/proforma-invoice/new');
  };

  const handleView = (item) => {
    navigate(`/sales/proforma-invoice/view/${item.id}`);
  };

  const handleEdit = (item) => {
    navigate(`/sales/proforma-invoice/edit/${item.id}`);
  };

  const handleApprove = (item) => {
    // Navigate to approve page (read-only edit mode)
    navigate(`/sales/proforma-invoice/approve/${item.id}`);
  };

  const handleConvert = async (item) => {
    try {
      setLoading(true);
      const response = await proformaInvoiceService.convertToTaxInvoice(item.id);
      if (response.success) {
        toast.success('Proforma invoice converted to tax invoice successfully');
        loadData();
        loadStats();
      } else {
        toast.error(response.message || 'Failed to convert proforma invoice');
      }
    } catch (error) {
      console.error('Error converting proforma invoice:', error);
      toast.error('Failed to convert proforma invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = (item) => {
    setCancelItem(item);
    setShowCancelModal(true);
  };

  const confirmCancel = async () => {
    if (!cancelItem) return;

    try {
      setLoading(true);
      const response = await proformaInvoiceService.updateProformaInvoice(cancelItem.id, {
        status: 'Cancelled'
      });

      if (response.success) {
        toast.success('Proforma invoice cancelled successfully');
        setShowCancelModal(false);
        setCancelItem(null);
        loadData();
        loadStats();
      } else {
        toast.error(response.message || 'Failed to cancel proforma invoice');
      }
    } catch (error) {
      console.error('Error cancelling proforma invoice:', error);
      toast.error('Failed to cancel proforma invoice');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = (item) => {
    setDeleteItem(item);
    setShowDeleteModal(true);
  };

  const confirmDelete = async () => {
    if (!deleteItem) return;

    try {
      setLoading(true);
      const invoiceId = deleteItem._id || deleteItem.id;
      if (!invoiceId) {
        toast.error('Invalid invoice ID');
        return;
      }
      
      const response = await proformaInvoiceService.deleteProformaInvoice(invoiceId);
      
      if (response.success) {
        toast.success('Proforma invoice deleted successfully');
        setShowDeleteModal(false);
        setDeleteItem(null);
        loadData();
        loadStats();
      } else {
        toast.error(response.message || 'Failed to delete proforma invoice');
      }
    } catch (error) {
      console.error('Error deleting proforma invoice:', error);
      const errorMessage = error.response?.data?.message || error.message || 'Failed to delete proforma invoice';
      toast.error(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const cancelDelete = () => {
    setShowDeleteModal(false);
    setDeleteItem(null);
  };

  const handleExport = () => {
    console.log('Exporting proforma invoices...');
    toast.success('Proforma invoices exported successfully');
  };

  // Breadcrumb items
  const breadcrumbItems = [
    {
      label: 'Sales',
      path: '/sales',
      onClick: () => navigate('/sales')
    },
    {
      label: 'Transactions',
      path: '/sales?tab=transactions',
      onClick: () => navigate('/sales?tab=transactions')
    },
    {
      label: 'Proforma Invoice',
      path: '/sales/proforma-invoice',
      isCurrent: true
    }
  ];

  return (
    <div className="sales-dashboard">
      {/* Breadcrumb Navigation */}
      <div className="breadcrumb-section">
        <Breadcrumb items={breadcrumbItems} />
      </div>

      {/* Page Header */}
      <div className="page-header">
        <div className="page-title">
          <h1>Proforma Invoice</h1>
          <p>Create preliminary invoices for approval</p>
        </div>
        <div className="page-actions">
          <button className="btn-primary" onClick={handleAddNew}>
            <Plus size={18} />
            Create Proforma Invoice
          </button>
        </div>
      </div>

      {/* Summary Cards */}
      <div className="dashboard-metrics-grid">
        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileCheck size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">{stats.totalInvoices}</div>
          <div className="metric-title">Total Invoices</div>
          <div className="metric-subtitle">All proforma invoices</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Calendar size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Draft</span>
            </div>
          </div>
          <div className="metric-value">{stats.draftInvoices}</div>
          <div className="metric-title">Draft Invoices</div>
          <div className="metric-subtitle">In preparation</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <CheckCircle size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Approved</span>
            </div>
          </div>
          <div className="metric-value">{stats.approvedInvoices || 0}</div>
          <div className="metric-title">Approved Invoices</div>
          <div className="metric-subtitle">Ready for sending</div>
        </div>

        <div className="metric-card churn">
          <div className="metric-header">
            <div className="metric-icon">
              <XCircle size={16} />
            </div>
            <div className="metric-trend neutral">
              <span>Cancelled</span>
            </div>
          </div>
          <div className="metric-value">{stats.cancelledInvoices || 0}</div>
          <div className="metric-title">Cancelled Invoices</div>
          <div className="metric-subtitle">Cancelled invoices</div>
        </div>

        <div className="metric-card burn">
          <div className="metric-header">
            <div className="metric-icon">
              <Building size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Total</span>
            </div>
          </div>
          <div className="metric-value">${stats.totalAmountWithTax.toLocaleString('en-US', { minimumFractionDigits: 0 })}</div>
          <div className="metric-title">Total Amount</div>
          <div className="metric-subtitle">All invoices combined</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <DollarSign size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Subtotal</span>
            </div>
          </div>
          <div className="metric-value">${(stats.totalValue || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}</div>
          <div className="metric-title">Invoice Amount</div>
          <div className="metric-subtitle">Total without tax</div>
        </div>

        <div className="metric-card customers">
          <div className="metric-header">
            <div className="metric-icon">
              <Hash size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Tax</span>
            </div>
          </div>
          <div className="metric-value">${(stats.totalTaxAmount || 0).toLocaleString('en-US', { minimumFractionDigits: 0 })}</div>
          <div className="metric-title">Total Tax</div>
          <div className="metric-subtitle">Total tax amount</div>
        </div>

        <div className="metric-card revenue">
          <div className="metric-header">
            <div className="metric-icon">
              <FileCheck size={16} />
            </div>
            <div className="metric-trend positive">
              <span>Converted</span>
            </div>
          </div>
          <div className="metric-value">{stats.convertedInvoices || 0}</div>
          <div className="metric-title">Converted</div>
          <div className="metric-subtitle">Converted to tax invoice</div>
        </div>
      </div>


      {/* Data Table */}
      <DataTable
        data={filteredData}
        columns={columns}
        loading={loading}
        onView={handleView}
        onEdit={handleEdit}
        onApprove={(row) => {
          // Only show approve action for Draft/Pending Approval invoices
          if (row.status === 'Draft' || row.status === 'Pending Approval') {
            handleApprove(row);
          }
        }}
        onCancel={(row) => {
          // Only show cancel action for Draft/Pending Approval invoices
          if (row.status === 'Draft' || row.status === 'Pending Approval') {
            handleCancel(row);
          }
        }}
        onDelete={handleDelete}
        onExport={handleExport}
        noCard={true}
        className="proforma-invoice-table"
        pageSize={10}
        customActions={(row) => {
          // If invoice is approved, only show Convert to Tax Invoice option
          if (row.status === 'Approved' && !row.convertedToTaxInvoice) {
            return [
              {
                type: 'convert',
                label: 'Convert to Tax Invoice',
                onClick: () => handleConvert(row)
              }
            ];
          }

          // For non-approved invoices, show all other actions
          const actions = [
            {
              type: 'view',
              label: 'View Details',
              onClick: () => handleView(row)
            },
            {
              type: 'edit',
              label: 'Edit',
              onClick: () => handleEdit(row)
            }
          ];

          // Add Approve action only for Draft/Pending Approval
          if (row.status === 'Draft' || row.status === 'Pending Approval') {
            actions.push({
              type: 'approve',
              label: 'Approve',
              icon: <CheckCircle size={16} />,
              onClick: () => handleApprove(row)
            });
          }

          // Add Cancel action only for Draft/Pending Approval
          if (row.status === 'Draft' || row.status === 'Pending Approval') {
            actions.push({
              type: 'cancel',
              label: 'Cancel',
              icon: <XCircle size={16} />,
              onClick: () => handleCancel(row)
            });
          }

          // Add Delete action for non-approved invoices
          actions.push({
            type: 'delete',
            label: 'Delete',
            onClick: () => handleDelete(row)
          });

          return actions;
        }}
      />

      {/* Confirmation Modals */}
      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => {
          setShowDeleteModal(false);
          setDeleteItem(null);
        }}
        onConfirm={confirmDelete}
        title="Delete Proforma Invoice"
        message={`Are you sure you want to delete invoice ${deleteItem?.proformaInvoiceNumber}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
      />

      <ConfirmationModal
        isOpen={showCancelModal}
        onClose={() => {
          setShowCancelModal(false);
          setCancelItem(null);
        }}
        onConfirm={confirmCancel}
        title="Cancel Proforma Invoice"
        message={`Are you sure you want to cancel invoice ${cancelItem?.proformaInvoiceNumber}? This action cannot be undone.`}
        confirmText="Cancel Invoice"
        cancelText="No, Keep It"
        type="warning"
      />
    </div>
  );
};

export default ProformaInvoice;

